import { storeToRefs } from "pinia";

export const useLoadUser = () => {
	const { getUserByEmail, isLoggedIn, logOut } = useNorcePwa();
	const { getUser } = storeToRefs(userStore());
	const router = useRouter();
	const localePath = useLocalePath();
	// useLoadUser is invoked from Pinia store actions (e.g. wishlistStore →
	// userStore.setUser), not only from component setup. useI18n() requires an
	// active setup context and throws "Must be called at the top of a `setup`
	// function" outside it, so read the locale via the Nuxt app context instead
	// — same pattern as wishlistStore.fetchWishlistProducts. The nuxtApp
	// reference is also kept to restore the context around setUser calls that
	// happen after an await (see getAndSetUser).
	const nuxtApp = useNuxtApp();
	const { locale } = nuxtApp.$i18n;

	const userHasSession = async () => {
		return await isLoggedIn(locale.value);
	};
	const loadUser = async () => {
		if (getUser.value && !Object.keys(getUser.value).length) {
			if (!(await userHasSession())) {
				return router.push(localePath("/login"));
			}
			await getAndSetUser();
		}
	};
	// setUser triggers a chain of store actions (wishlist sync) that call
	// composables. When we get here after an await, the Nuxt context is gone
	// on the server — runWithContext restores it for the synchronous start of
	// that chain, where the actions capture what they need (SSR-safe).
	const setUserInContext = (
		user: Parameters<ReturnType<typeof userStore>["setUser"]>[0]
	) => nuxtApp.runWithContext(() => userStore().setUser(user));

	// Logged-out cleanup: dropping the stale user must never take the page
	// down — setUser cascades into the wishlist sync, and a transient API
	// failure there is not a reason to fail SSR (account.vue has no try/catch
	// around layout init). Fire, log, move on.
	const clearUserNonFatal = async () => {
		try {
			await setUserInContext(null);
		} catch (error) {
			console.warn(
				"[useLoadUser] post-logout cleanup failed (non-fatal):",
				error instanceof Error ? error.message : error
			);
		}
	};

	const getUserByEmailIfHasSession = async () => {
		if (await userHasSession()) {
			try {
				return await getAndSetUser();
			} catch (error) {
				if (isNorcePwaError(error)) {
					if (error.statusCode === ErrorStatusCode.UNAUTHORIZED) {
						await logOut();
						await clearUserNonFatal();
					}
				}
				return null;
			}
		}
		// No valid session cookie — drop any stale persisted user so the UI
		// can't stay "logged in" from localStorage after the cookie is gone.
		await clearUserNonFatal();
		return null;
	};

	const getAndSetUser = async () => {
		const user = await getUserByEmail({ localeCode: locale.value });
		if (user) {
			await setUserInContext(user);
		} else {
			// Valid session cookie, but no account in the current locale's Norce
			// application (e.g. registered on SE, now browsing US). Treat as logged
			// out for THIS application — but keep the cookie so the original app's
			// session still works when switching back.
			await setUserInContext(null);
		}
		return user ?? null;
	};
	return { loadUser, userHasSession, getUserByEmailIfHasSession };
};
